// 推荐
import { computed, Ref } from '@vue/composition-api';
import {
  InterestBehaviorScene,
  AdgroupFormData,
  CampaignFormData,
  DefaultVideoActionsV2,
  DefaultActionDaysV2,
} from '@tt4b/ttam-enums';
import { getCurrentLang } from '@tt4b/ttam-utils';
import { uniq } from 'lodash-es';
import { useBehaviorData } from './../../../../hooks/creation/adgroup/useBehavior/index';
import store from './store';
import { getIdItemMap } from './../../../../utils/index';
import {
  useRecommendInterestKeywordsDataSource,
  useRecommendTargetTrack,
} from '../../recommend-interest-behavior/hooks';
import {
  useInterestCategoryChildrenMerger,
  useCommonInterestsCategoryPropsOptions,
} from '../../interests/hooks/common-interests-category';
import { useStore } from '@tt4b/ttam-hooks';
import { useInterest } from './use-interest';
const { state: interestState } = store;
const { INTEREST, CREATOR, VIDEO, KEYWORD } = InterestBehaviorScene;

export function useTargetRecommendClickable(props: {
  ctx: any;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { ctx, adgroupData, campaignData } = props;
  const adTagV2 = computed(() => adgroupData.value.adTagV2);
  const interestKeywordsI18n = computed(() => adgroupData.value.interestKeywordsI18n);
  const { interestItemMap } = useInterest();
  const {
    videoCategories,
    creatorCategories,
    videoActions,
    videoDay,
    creatorActions,
    videoData,
    getChangedBehaviorData,
  } = useBehaviorData({
    adgroupData,
  });
  const { recommendInterestKeywordsItemMap, resetInterestKeywords } = useRecommendInterestKeywordsDataSource({
    adgroupData,
  });
  const { updateTraceInfo, recommendData } = useRecommendTargetTrack({
    adgroupData,
    campaignData,
  });
  const { options: commonInterestsOptions } = useCommonInterestsCategoryPropsOptions();
  const { mergeInterestCategoryChildrenIds: mergeInterestCategoryChildren } = useInterestCategoryChildrenMerger({
    interestOptions: commonInterestsOptions,
  });
  const { state } = useStore();

  const interestKeywordsInfoMap = computed(() => state.targetInfo.value?.interestKeywordsInfoMap ?? {});

  const videoBehaviorItemMap = computed(() => getIdItemMap(interestState.videoBehaviorList.value));
  const creatorBehaviorItemMap = computed(() => getIdItemMap(interestState.creatorBehaviorList.value));

  function formatTagNode(ids, nodeMap, scene) {
    let prop = { label: 'label' };

    if (scene === INTEREST) {
      prop = { label: 'i18n_value' };
    }

    if (scene === KEYWORD) {
      prop = { label: 'keyword' };
    }

    return ids?.map(id => ({
      ...nodeMap[id],
      scene,
      tag_name: nodeMap[id]?.[prop.label],
      id,
    }));
  }

  function filterNotSeletedTagNode(
    scene:
      | InterestBehaviorScene.INTEREST
      | InterestBehaviorScene.KEYWORD
      | InterestBehaviorScene.VIDEO
      | InterestBehaviorScene.CREATOR,
  ) {
    if (!interestState.interestBehaviorList.value.length) {
      return [];
    }
    const { interest, behavior = {}, interestKeywords = [] } = recommendData.value;
    const { video = [], creator = [] } = behavior;
    const interestRelatedIds = interest.map(v => v.value);
    const videoRelatedIds = video.map(v => v.value);
    const creatorRelatedIds = creator.map(v => v.value);

    const interestKeywordsRelatedIds = interestKeywords.map(v => v.value);
    let selectedNodes: number[] = [];
    let nodeMap = {};
    let recommendIds: number[] = [];

    if (scene === INTEREST) {
      selectedNodes = adTagV2.value;
      nodeMap = interestItemMap.value;
      recommendIds = interestRelatedIds;
    }

    if (scene === KEYWORD) {
      selectedNodes = interestKeywordsI18n.value;
      nodeMap = recommendInterestKeywordsItemMap.value;
      recommendIds = interestKeywordsRelatedIds;
    }

    if (scene === VIDEO) {
      selectedNodes = videoCategories.value;
      nodeMap = videoBehaviorItemMap.value;
      recommendIds = videoRelatedIds;
    }

    if (scene === CREATOR) {
      selectedNodes = creatorCategories.value;
      nodeMap = creatorBehaviorItemMap.value;
      recommendIds = creatorRelatedIds;
    }

    const recommendNodes = formatTagNode(recommendIds, nodeMap, scene);
    // 过滤掉包含的node
    const droppedNodes = recommendNodes
      ?.filter(item => !selectedNodes?.includes(item?.id))
      ?.filter(item => {
        let parent = item;
        let isAdopted = false;
        while (parent) {
          const pid = parent?.id;
          parent = nodeMap[pid]?.parent;
          if (selectedNodes?.includes(parent?.id)) {
            isAdopted = true;
            break;
          }
        }

        return !isAdopted;
      });
    return droppedNodes;
  }

  /** 所有推荐标签item数组 */
  const recommendFlattenData = computed(() => [
    ...filterNotSeletedTagNode(INTEREST),
    ...filterNotSeletedTagNode(KEYWORD),
    ...filterNotSeletedTagNode(VIDEO),
    ...filterNotSeletedTagNode(CREATOR),
  ]);

  function handleTagSelected(scene, tagList) {
    // const needUnfoldBlock: string[] = [];
    // 更新定向推荐展示列表 && setFormData
    let tempData = {};
    if (scene === INTEREST) {
      const data = mergeInterestCategoryChildren(uniq([...adTagV2.value, ...tagList]));
      tempData = {
        ...tempData,
        adTagV2: data || [],
      };
      // needUnfoldBlock.push('interest');
    }

    if (scene === KEYWORD) {
      resetInterestKeywords();
      const data = uniq([...interestKeywordsI18n.value, ...tagList]);

      const interestKeywordsLangI18nTemp: string[] = [];
      const currentLang = getCurrentLang();
      data.forEach(id => {
        const lang = interestKeywordsInfoMap.value[id].lang || currentLang;
        interestKeywordsLangI18nTemp.push(lang);
      });
      tempData = {
        ...tempData,
        interestKeywordsI18n: data || [],
      };
      // needUnfoldBlock.push('interest');
    }

    if (scene === VIDEO) {
      let data: any = {};
      data.categories = uniq([...videoCategories.value, ...tagList]);
      if (!videoActions.value?.length) {
        data.actions = DefaultVideoActionsV2[VIDEO]().map(item => item.value);
      }

      if (!videoDay.value) {
        data.day = DefaultActionDaysV2[VIDEO].id;
      }
      data = getChangedBehaviorData(
        {
          ...videoData.value,
          ...data,
        },
        'video',
      );
      tempData = { ...tempData, ...data };
      // needUnfoldBlock.push('video');
    }

    if (scene === CREATOR) {
      const data: any = {};
      data.categories = uniq([...creatorCategories.value, ...tagList]);
      if (!creatorActions.value?.length) {
        data.actions = DefaultVideoActionsV2[CREATOR]().map(item => item.value);
      }
      tempData = { ...tempData, ...data };
      // needUnfoldBlock.push('creator');
    }

    return tempData;
  }
  // 选择一个
  function selectOneTag(tag) {
    const { scene } = tag;
    const res = handleTagSelected(scene, [tag.id]);
    ctx && ctx.emit('change', res);
    updateTraceInfo('fromSingleTag');
  }
  // 选择全部
  function selectAll() {
    const { interest, behavior = {}, interestKeywords = [] } = recommendData.value;
    const { video = [], creator = [] } = behavior;
    const videoRelatedIds = video.map(v => v.value);
    const creatorRelatedIds = creator.map(v => v.value);
    const interestIds = interest.map(v => v.value);
    const interestKeywordsIds = interestKeywords.map(v => v.value);
    if (interestIds?.length) {
      const data = handleTagSelected(INTEREST, interestIds);
      ctx && ctx.emit('change', data);
    }

    if (interestKeywordsIds?.length) {
      const data = handleTagSelected(KEYWORD, interestKeywordsIds);
      ctx && ctx.emit('change', data);
    }

    if (videoRelatedIds?.length) {
      const data = handleTagSelected(VIDEO, videoRelatedIds);
      ctx && ctx.emit('change', data);
    }

    if (creatorRelatedIds?.length) {
      const data = handleTagSelected(CREATOR, creatorRelatedIds);
      ctx && ctx.emit('change', data);
    }
    updateTraceInfo('fromSelectAll');
  }
  // 是否展示显示所有
  const itemCount = computed(() => recommendFlattenData.value?.length);

  return {
    selectOneTag,
    selectAll,
    itemCount,
    recommendFlattenData,
  };
}
